// Pure payload builders extracted from ProblemForm for testability (US1760811 PR review #4).
// Payload shapes match the original inline object literals in ProblemForm.js, plus
// slackSmsCommunication on both builders and outage on the peer-review one; the send payload
// must never gain outage (AttachCommunications stores it as-is). Inputs may be null (e.g. outage before load), so possibly
// -null accesses are optional-chained to yield "" / undefined rather than throwing.

// buildReportedImpactsPayload snapshots reported impacts into the comms/peer-review payload shape.
export const buildReportedImpactsPayload = (impacts) =>
  (impacts || []).map((ri) => ({
    brand: ri.brand || "",
    workload: ri.applicationName || "",
    incidentTicket: ri.childIncidentNumber || "",
    status: ri.status || "",
  }));

// buildSendCommunicationPayload builds the payload posted via CommunicationAPI.attachCommunication.
export const buildSendCommunicationPayload = ({
  outage,
  priorityOption,
  templateOption,
  incidentCommander,
  reportedIssue,
  slackSmsCommunication,
  nextUpdate,
  incidentStart,
  incidentEnd,
  eocNotified,
  timeDetected,
  techTimeResolved,
  applications,
  publishingTargets,
  sender,
  reportedImpacts,
}) => ({
  nextUpdate: nextUpdate ? nextUpdate.getTime() : 0,
  reportedIssue: reportedIssue,
  slackSmsCommunication: slackSmsCommunication,
  slackChannel: outage?.slackChannel,
  incidentCommander: incidentCommander ? incidentCommander.label : "",
  incidentManager: outage && outage.mim ? outage.mim : "None",
  priority: priorityOption?.label || "",
  clientImpact: outage && outage.impact ? outage.impact : "",
  incidentStart: incidentStart ? incidentStart.getTime() : 0,
  incidentEnd: incidentEnd ? incidentEnd.getTime() : 0,
  eocNotified: eocNotified ? eocNotified.getTime() : 0,
  timeDetected: timeDetected ? timeDetected.getTime() : 0,
  numberOfContacts:
    outage && outage.numberOfContacts ? outage.numberOfContacts : "",
  workaround: outage && outage.workaround ? outage.workaround : "",
  messageForClients:
    outage && outage.messageForClients ? outage.messageForClients : "",
  affectedUsers: outage && outage.affectedUsers ? outage.affectedUsers : "",
  currentApproach:
    outage && outage.currentApproach ? outage.currentApproach : "",
  salesForceCase:
    outage && outage.salesForceCase ? outage.salesForceCase : "",
  perceivedCause:
    outage && outage.perceivedCause ? outage.perceivedCause : "",
  technicalStatus:
    outage && outage.technicalStatus ? outage.technicalStatus : "",
  techTimeResolved: techTimeResolved ? techTimeResolved.getTime() : 0,
  vendorTicket: outage && outage.vendorTicket ? outage.vendorTicket : "",
  awsAccount: outage && outage.awsAccount ? outage.awsAccount : "",
  currentStatus: outage?.description || "",
  subject: outage?.short_description || "",
  status: "SEND",
  applications: applications,
  template: templateOption && templateOption.label,
  statusPageRoutes: publishingTargets,
  sender: sender,
  reportedImpacts: buildReportedImpactsPayload(reportedImpacts),
});

// buildPeerReviewPayload builds the payload posted to the peer-review endpoint.
export const buildPeerReviewPayload = ({
  outage,
  priorityOption,
  templateOption,
  incidentCommander,
  affectedUserOption,
  slackSmsCommunication,
  nextUpdate,
  incidentStart,
  incidentEnd,
  eocNotified,
  timeDetected,
  techTimeResolved,
  reportedImpacts,
}) => ({
  // RequestPeerReview threads the card under the review message, which needs the outage to
  // build a CardSource, and the Slack/SMS text for the card's line 3.
  outage: outage?.number,
  slackSmsCommunication: slackSmsCommunication,
  affectedUsers: affectedUserOption?.label,
  nextUpdate: nextUpdate ? nextUpdate.getTime() : 0,
  slackChannel: outage && outage.slackChannel,
  incidentCommander:
    incidentCommander && incidentCommander.label
      ? incidentCommander.label
      : "",
  incidentManager: outage && outage.mim ? outage.mim : "None",
  priority: priorityOption?.label || "",
  clientImpact: outage && outage.impact ? outage.impact : "",
  incidentStart: incidentStart ? incidentStart.getTime() : 0,
  incidentEnd: incidentEnd ? incidentEnd.getTime() : 0,
  eocNotified: eocNotified ? eocNotified.getTime() : 0,
  timeDetected: timeDetected ? timeDetected.getTime() : 0,
  numberOfContacts:
    outage && outage.numberOfContacts ? outage.numberOfContacts : "",
  workaround: outage && outage.workaround ? outage.workaround : "",
  template: templateOption && templateOption.label,
  messageForClients:
    outage && outage.messageForClients ? outage.messageForClients : "",
  currentApproach:
    outage && outage.currentApproach ? outage.currentApproach : "",
  salesForceCase:
    outage && outage.salesForceCase ? outage.salesForceCase : "",
  perceivedCause:
    outage && outage.perceivedCause ? outage.perceivedCause : "",
  technicalStatus:
    outage && outage.technicalStatus ? outage.technicalStatus : "",
  techTimeResolved: techTimeResolved ? techTimeResolved.getTime() : 0,
  vendorTicket: outage && outage.vendorTicket ? outage.vendorTicket : "",
  awsAccount: outage && outage.awsAccount ? outage.awsAccount : "",
  subject: outage?.short_description || "",
  currentStatus: outage?.description || "",
  applications:
    outage && outage.impactedApplications
      ? outage.impactedApplications.map((o) => o.label)
      : [],
  affectedBrandCapability:
    outage && outage.affectedBrandCapability
      ? outage.affectedBrandCapability.map((o) => o)
      : [],
  reportedImpacts: buildReportedImpactsPayload(reportedImpacts),
});
